/**
 * CommunityTipsView - Visualização de dicas da comunidade
 * 
 * Extraído do App.js durante refatoração Fase 2
 * 
 * Funcionalidades:
 * - Listagem de dicas da comunidade
 * - Filtro por categoria
 * - Sistema de likes
 * - Sistema de comentários
 * - Criação de novas dicas
 * - Modal de exemplo
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - useAuth (autenticação)
 * - axios (requisições API)
 * - Card, Button, Badge, Select (shadcn/ui)
 * - ExampleModal (componente extraído)
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useTranslation } from "../translations";
import { useAuth } from "../App";
import ExampleModal from "./ExampleModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const parseTipContent = (tip) => {
  let content = tip.content || '';
  let sourceUrl = tip.source_url || null;
  let sourceName = tip.source_name || null;
  const m = content.match(/\n*🔗 Fonte:\s*(.*?)\s*—\s*(https?:\/\/\S+)/);
  if (m) {
    sourceName = sourceName || m[1].trim();
    sourceUrl = sourceUrl || m[2].trim();
    content = content.replace(m[0], '').trim();
  }
  return { content, sourceUrl, sourceName };
};

const CommunityTipsView = () => {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedTip, setSelectedTip] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [commentImages, setCommentImages] = useState([]);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [userLikes, setUserLikes] = useState(new Set());
  const [forceUpdate, setForceUpdate] = useState(0);
  const [showExample, setShowExample] = useState(false);
  const [showTip, setShowTip] = useState(true);
  const [showNewTipModal, setShowNewTipModal] = useState(false);
  const [newTipData, setNewTipData] = useState({ title: '', content: '', category: 'outros', images: [] });
  const [submittingTip, setSubmittingTip] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreviewModal, setImagePreviewModal] = useState({ open: false, src: '' });
  const { user } = useAuth();
  const { t } = useTranslation();

  // Max images allowed
  const MAX_IMAGES = 3;
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

  // Handle image upload
  const handleImageUpload = async (e, isComment = false) => {
    const files = Array.from(e.target.files);
    const currentImages = isComment ? commentImages : newTipData.images;
    
    if (currentImages.length + files.length > MAX_IMAGES) {
      alert(`${t('ctMaxImages')} (${MAX_IMAGES})`);
      return;
    }
    
    setUploadingImage(true);
    
    for (const file of files) {
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) {
        alert(t('ctFormatNotSupported'));
        continue;
      }
      
      // Validate file size
      if (file.size > MAX_IMAGE_SIZE) {
        alert(t('ctImageTooLarge'));
        continue;
      }
      
      try {
        // Convert to base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        
        // Upload to server
        const response = await axios.post(`${API}/upload/community-image`, {
          image: base64
        });
        
        const imageUrl = response.data.url;
        
        if (isComment) {
          setCommentImages(prev => [...prev, imageUrl]);
        } else {
          setNewTipData(prev => ({
            ...prev,
            images: [...prev.images, imageUrl]
          }));
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        alert(t('ctErrorUploadImage'));
      }
    }
    
    setUploadingImage(false);
    e.target.value = ''; // Reset input
  };

  // Remove image
  const removeImage = (index, isComment = false) => {
    if (isComment) {
      setCommentImages(prev => prev.filter((_, i) => i !== index));
    } else {
      setNewTipData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  useEffect(() => {
    let isMounted = true;
    
    const fetchCommunityTips = async () => {
      try {
        if (!isMounted) return;
        
        setLoading(true);
        const response = await axios.get(`${API}/community-tips?category=${selectedCategory}`);
        
        if (isMounted) {
          setTips(response.data);  
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching community tips:", error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCommunityTips();
    return () => {
      isMounted = false;
    };
  }, [selectedCategory]);

  const handleLike = React.useCallback(async (tipId) => {
    const currentTip = tips.find(t => t.id === tipId);
    if (!currentTip) return;
    
    try {
      console.log("LIKE FUNCTION CALLED - Attempting to like tip:", tipId);
      
      const isCurrentlyLiked = userLikes.has(tipId);
      const newLikeCount = isCurrentlyLiked ? currentTip.likes - 1 : currentTip.likes + 1;
      
      console.log(`INSTANT UPDATE: ${currentTip.likes} -> ${newLikeCount} likes`);
      
      setTips(prevTips => 
        prevTips.map(tip => 
          tip.id === tipId 
            ? { ...tip, likes: Math.max(0, newLikeCount) }
            : tip
        )
      );
      
      setUserLikes(prevLikes => {
        const newLikes = new Set(prevLikes);
        if (isCurrentlyLiked) {
          newLikes.delete(tipId);
        } else {
          newLikes.add(tipId);
        }
        return newLikes;
      });
      
      setForceUpdate(prev => prev + 1);
      setTimeout(() => setForceUpdate(prev => prev + 1), 10);
      setTimeout(() => setForceUpdate(prev => prev + 1), 50);
      setTimeout(() => {
        setForceUpdate(prev => prev + 1);
        window.scrollBy(0, 1);
        window.scrollBy(0, -1);
      }, 100);
      
      const response = await axios.post(`${API}/community-tips/${tipId}/like`);
      console.log("Server confirmed like:", response.data.liked, "New count should be visible NOW");
      
    } catch (error) {
      console.error("Error with like:", error);
      setTips(prevTips => 
        prevTips.map(tip => 
          tip.id === tipId 
            ? { ...tip, likes: currentTip.likes }
            : tip
        )
      );
      setForceUpdate(prev => prev + 1);
      alert(t('errorLikeTip'));
    }
  }, [tips, userLikes, t]);

  const handleComment = React.useCallback(async (tip) => {
    console.log("COMMENT FUNCTION CALLED - handleComment called with tip:", tip.id, tip.title);
    setSelectedTip(tip);
    setCommentDialogOpen(true);
    setComments([]);
    setLoadingComments(true);
    
    // Fetch existing comments for this tip
    try {
      const response = await axios.get(`${API}/community-tips/${tip.id}/comments`);
      setComments(response.data || []);
    } catch (error) {
      console.error("Error fetching comments:", error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, []);

  const submitComment = React.useCallback(async () => {
    // Permitir envio se tiver texto OU imagem
    if ((!newComment.trim() && commentImages.length === 0) || !selectedTip) return;
    
    try {
      console.log("SUBMIT COMMENT - Attempting to create comment on tip:", selectedTip.id);
      const response = await axios.post(`${API}/community-tips/${selectedTip.id}/comments`, {
        content: newComment.trim() || '📷', // Se não tiver texto, usa emoji de foto
        images: commentImages
      });
      console.log("Comment response:", response.data);
      
      // Add new comment to local list
      const newCommentData = {
        ...response.data,
        author_name: user?.name || user?.email || 'Você',
        images: commentImages
      };
      setComments(prev => [...prev, newCommentData]);
      
      // Update tip's comment count
      setTips(prevTips => 
        prevTips.map(tip => 
          tip.id === selectedTip.id 
            ? { ...tip, comments_count: (tip.comments_count || 0) + 1 }
            : tip
        )
      );
      
      setNewComment("");
      setCommentImages([]);
      console.log("Comment processed successfully - UI updated immediately");
      
    } catch (error) {
      console.error("Error creating comment:", error);
      console.error("Error details:", error.response?.data);
      alert(t('ctErrorAddComment') + ": " + (error.response?.data?.detail || error.message));
    }
  }, [newComment, selectedTip, user, commentImages]);

  const createNewTip = async () => {
    setShowNewTipModal(true);
  };

  const submitNewTip = async () => {
    if (!newTipData.title.trim() || !newTipData.content.trim()) {
      alert(t('ctFillTitleContent'));
      return;
    }
    
    setSubmittingTip(true);
    
    try {
      const response = await axios.post(`${API}/community-tips`, {
        title: newTipData.title.trim(),
        content: newTipData.content.trim(),
        category: newTipData.category.toLowerCase().trim(),
        images: newTipData.images
      });
      
      // Add new tip with author name
      const tipWithAuthor = {
        ...response.data,
        author_name: user?.name || user?.email || 'Você'
      };
      
      setTips(prevTips => [tipWithAuthor, ...prevTips]);
      setShowNewTipModal(false);
      setNewTipData({ title: '', content: '', category: 'outros', images: [] });
      alert(t('tipShared'));
      
    } catch (error) {
      console.error("Error creating tip:", error);
      alert(t('ctErrorShareTip') + ": " + (error.response?.data?.detail || error.message));
    } finally {
      setSubmittingTip(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">{t('loadingCommunityTips')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Modal de Exemplo */}
      <ExampleModal isOpen={showExample} onClose={() => setShowExample(false)} type="comunidade" />

      {/* Tooltip Como Usar */}
      {showTip && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 relative">
          <button 
            onClick={() => setShowTip(false)}
            className="absolute top-2 right-2 text-green-400 hover:text-green-600"
          >
            ✕
          </button>
          <div className="flex items-start gap-2 pr-6">
            <span className="text-lg">💬</span>
            <div>
              <h4 className="font-semibold text-green-800">{t('howToUse')}</h4>
              <p className="text-sm text-green-700 mt-1">{t('tooltipCommunity')}</p>
            </div>
          </div>
        </div>
      )}

      <div className="text-center">
        <h2 className="text-2xl font-bold text-green-800 mb-2">💬 {t('communityTipsTitle')}</h2>
        <p className="text-gray-600">{t('communityTipsSubtitle')}</p>
      </div>

      {/* Botão Ver Exemplo */}
      <div className="flex justify-center">
        <button
          onClick={() => setShowExample(true)}
          className="py-2 px-4 text-sm font-medium text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors flex items-center gap-1 border border-green-200"
        >
          💡 {t('seeExampleTip')}
        </button>
      </div>

      {/* Category Filter */}
      <div className="flex justify-center">
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t('filterByCategory')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allCategories')}</SelectItem>
            <SelectItem value="sangria">{t('categoryBleeding')}</SelectItem>
            <SelectItem value="doencas">{t('categoryDiseases')}</SelectItem>
            <SelectItem value="ideias">{t('categoryIdeas')}</SelectItem>
            <SelectItem value="mercado">{t('categoryMarket')}</SelectItem>
            <SelectItem value="noticias">{t('categoryNews')}</SelectItem>
            <SelectItem value="clima">{t('categoryWeather')}</SelectItem>
            <SelectItem value="outros">{t('categoryOthers')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tips Grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {tips.map((tip) => {
          const parsed = parseTipContent(tip);
          return (
          <Card key={`tip-${tip.id}`} className="hover:shadow-md transition-shadow overflow-hidden">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{tip.title}</CardTitle>
                  <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
                    <span>{t('by')} {tip.author_name || tip.author}</span>
                    <span>•</span>
                    <span>{new Date(tip.created_at).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {tip.is_example && (
                    <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs">
                      📘 {t('exampleBadge')}
                    </Badge>
                  )}
                  <Badge variant="outline">{tip.category}</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-3 break-words whitespace-pre-line">{parsed.content}</p>
              {parsed.sourceUrl && (
                <a
                  href={parsed.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:underline mb-4"
                  data-testid={`tip-source-link-${tip.id}`}
                >
                  🔗 Ver fonte{parsed.sourceName ? `: ${parsed.sourceName}` : ''}
                </a>
              )}
              
              {/* Images Gallery */}
              {tip.images && tip.images.length > 0 && (
                <div className="mb-4">
                  {tip.images.length === 1 ? (
                    // Single image - show complete
                    <div 
                      className="relative cursor-pointer rounded-lg overflow-hidden bg-gray-100 border border-gray-200 inline-block"
                      onClick={() => setImagePreviewModal({ open: true, src: tip.images[0].startsWith('/api') ? `${BACKEND_URL}${tip.images[0]}` : tip.images[0] })}
                    >
                      <img 
                        src={tip.images[0].startsWith('/api') ? `${BACKEND_URL}${tip.images[0]}` : tip.images[0]}
                        alt="Imagem da dica"
                        className="max-w-full max-h-80 object-contain hover:opacity-90 transition-opacity"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 rounded-full px-2 py-1">
                        <span className="text-white text-xs">🔍 Ampliar</span>
                      </div>
                    </div>
                  ) : (
                    // Multiple images - grid layout
                    <div className={`grid gap-2 ${tip.images.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                      {tip.images.map((img, idx) => (
                        <div 
                          key={idx} 
                          className="relative cursor-pointer rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
                          onClick={() => setImagePreviewModal({ open: true, src: img.startsWith('/api') ? `${BACKEND_URL}${img}` : img })}
                        >
                          <img 
                            src={img.startsWith('/api') ? `${BACKEND_URL}${img}` : img}
                            alt={`Imagem ${idx + 1}`}
                            className="w-full h-32 object-cover hover:opacity-90 transition-opacity"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div className="absolute bottom-1 right-1 bg-black bg-opacity-50 rounded-full p-1">
                            <span className="text-white text-xs">🔍</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <button 
                  key={`like-${tip.id}-${tip.likes}-${forceUpdate}`}
                  className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground px-3 py-2 ${userLikes.has(tip.id) ? 'text-green-700 bg-green-50' : 'text-green-600'} hover:text-green-700`}
                  onClick={() => handleLike(tip.id)}
                  onMouseDown={() => {
                    console.log("MOUSEDOWN: Like button for tip:", tip.id);
                  }}
                  style={{cursor: 'pointer'}}
                >
                  👍 {tip.likes || 0} {t('likes')}
                </button>
                <button 
                  key={`comment-${tip.id}-${tip.comments_count}-${forceUpdate}`}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground px-3 py-2 text-blue-600 hover:text-blue-700"
                  onClick={() => handleComment(tip)}
                  style={{cursor: 'pointer'}}
                >
                  💬 {tip.comments_count || 0} {t('comments')}
                </button>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>

      {tips.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          {t('noProductsFound')}
        </div>
      )}

      {/* Add Tip Button */}
      <div className="text-center">
        <Button 
          className="bg-green-600 hover:bg-green-700"
          onClick={createNewTip}
        >
          ➕ {t('shareATip')}
        </Button>
      </div>

      {/* Comment Dialog - Modal Visual */}
      {commentDialogOpen && selectedTip && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" 
          style={{zIndex: 99999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0}}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 shadow-2xl border-2 border-blue-500 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-blue-800">💬 Comentários</h3>
              <p className="text-sm text-gray-600">{selectedTip?.title || 'Dica da Comunidade'}</p>
            </div>
            
            {/* Comments List */}
            <div className="flex-1 overflow-y-auto mb-4 max-h-64 border rounded-md p-3 bg-gray-50">
              {loadingComments ? (
                <div className="text-center py-4 text-gray-500">
                  <span className="animate-pulse">{t('ctLoadingComments')}</span>
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  <p>{t('ctNoComments')}</p>
                  <p className="text-sm">{t('ctBeFirst')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {comments.map((comment, index) => (
                    <div key={comment.id || index} className="bg-white p-3 rounded-lg shadow-sm border">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium text-sm text-green-700">
                          {comment.author_name || 'Usuário'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {comment.created_at ? new Date(comment.created_at).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : ''}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{comment.content}</p>
                      {/* Comment Images */}
                      {comment.images && comment.images.length > 0 && (
                        <div className="mt-2">
                          {comment.images.length === 1 ? (
                            // Single image - show larger
                            <div 
                              className="relative inline-block cursor-pointer rounded-lg overflow-hidden border border-gray-200"
                              onClick={() => setImagePreviewModal({ open: true, src: comment.images[0].startsWith('/api') ? `${BACKEND_URL}${comment.images[0]}` : comment.images[0] })}
                            >
                              <img 
                                src={comment.images[0].startsWith('/api') ? `${BACKEND_URL}${comment.images[0]}` : comment.images[0]}
                                alt="Imagem do comentário"
                                className="max-w-xs max-h-48 object-contain hover:opacity-90 transition-opacity"
                              />
                              <div className="absolute bottom-1 right-1 bg-black bg-opacity-50 rounded-full px-2 py-0.5">
                                <span className="text-white text-xs">🔍</span>
                              </div>
                            </div>
                          ) : (
                            // Multiple images - smaller grid
                            <div className="flex gap-2 flex-wrap">
                              {comment.images.map((img, imgIdx) => (
                                <div 
                                  key={imgIdx}
                                  className="relative cursor-pointer rounded-lg overflow-hidden border border-gray-200"
                                  onClick={() => setImagePreviewModal({ open: true, src: img.startsWith('/api') ? `${BACKEND_URL}${img}` : img })}
                                >
                                  <img 
                                    src={img.startsWith('/api') ? `${BACKEND_URL}${img}` : img}
                                    alt={`Imagem ${imgIdx + 1}`}
                                    className="w-24 h-24 object-cover hover:opacity-80"
                                  />
                                  <div className="absolute bottom-0.5 right-0.5 bg-black bg-opacity-50 rounded-full p-0.5">
                                    <span className="text-white text-xs">🔍</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* New Comment Form */}
            <div className="space-y-3 border-t pt-3">
              <textarea
                placeholder="Digite seu comentário aqui..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={3}
                className="w-full p-3 border-2 border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              
              {/* Comment Image Upload */}
              <div>
                {/* Preview uploaded images */}
                {commentImages.length > 0 && (
                  <div className="flex gap-2 mb-2 flex-wrap">
                    {commentImages.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img 
                          src={img.startsWith('/api') ? `${BACKEND_URL}${img}` : img}
                          alt={`Preview ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded border-2 border-blue-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(idx, true)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload button */}
                {commentImages.length < MAX_IMAGES && (
                  <label className="cursor-pointer inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
                    <span>📷</span>
                    <span>{uploadingImage ? t('ctSending') : t('ctAddPhoto')}</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={(e) => handleImageUpload(e, true)}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                  </label>
                )}
              </div>
              
              <div className="flex justify-end space-x-2">
                <button 
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setCommentDialogOpen(false);
                    setNewComment("");
                    setSelectedTip(null);
                    setComments([]);
                    setCommentImages([]);
                  }}
                >
                  {t('close')}
                </button>
                <button 
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  onClick={submitComment}
                  disabled={(!newComment.trim() && commentImages.length === 0) || uploadingImage}
                >
                  {uploadingImage ? t('ctSending') : t('ctSendComment')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Tip Modal */}
      {showNewTipModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" 
          style={{zIndex: 99999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0}}
        >
          <div className="bg-white rounded-lg w-full max-w-lg shadow-2xl border-2 border-green-500 max-h-[90vh] flex flex-col">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold text-green-800">➕ Compartilhar uma Dica</h3>
              <p className="text-sm text-gray-600">{t('ctShareKnowledge')}</p>
            </div>
            
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título da Dica *
                </label>
                <input
                  type="text"
                  placeholder="Ex: Como aumentar a produção em dias quentes"
                  value={newTipData.title}
                  onChange={(e) => setNewTipData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full p-3 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  maxLength={100}
                />
              </div>
              
              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conte sua dica ou experiência *
                </label>
                <textarea
                  placeholder="Descreva sua dica com detalhes para ajudar outros profissionais..."
                  value={newTipData.content}
                  onChange={(e) => setNewTipData(prev => ({ ...prev, content: e.target.value }))}
                  rows={4}
                  className="w-full p-3 border-2 border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  maxLength={1000}
                />
                <p className="text-xs text-gray-400 mt-1">{newTipData.content.length}/1000 caracteres</p>
              </div>
              
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={newTipData.category}
                  onChange={(e) => setNewTipData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full p-3 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="sangria">🌿 Sangria</option>
                  <option value="doencas">🦠 Doenças</option>
                  <option value="ideias">💡 Ideias</option>
                  <option value="mercado">📈 Mercado da Borracha</option>
                  <option value="noticias">📰 Notícias</option>
                  <option value="clima">🌤️ Clima</option>
                  <option value="outros">📝 Outros</option>
                </select>
              </div>
              
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📷 Fotos (opcional - máx. 3)
                </label>
                
                {/* Image Preview */}
                {newTipData.images.length > 0 && (
                  <div className="flex gap-2 mb-2 flex-wrap">
                    {newTipData.images.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img 
                          src={img.startsWith('/api') ? `${BACKEND_URL}${img}` : img}
                          alt={`Preview ${idx + 1}`}
                          className="w-20 h-20 object-cover rounded-lg border-2 border-green-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(idx, false)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload Button */}
                {newTipData.images.length < MAX_IMAGES && (
                  <label className="cursor-pointer">
                    <div className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors ${uploadingImage ? 'opacity-50' : ''}`}>
                      {uploadingImage ? (
                        <span className="text-gray-500">{t('ctSending')}</span>
                      ) : (
                        <>
                          <span className="text-2xl">📷</span>
                          <span className="text-sm text-gray-600">{t('ctClickAddPhotos')}</span>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={(e) => handleImageUpload(e, false)}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                  </label>
                )}
                <p className="text-xs text-gray-400 mt-1">JPG, PNG ou WEBP • Máx. 5MB cada</p>
              </div>
            </div>
              
            {/* Buttons - Fixed at bottom */}
            <div className="flex justify-end space-x-2 p-4 border-t bg-gray-50 rounded-b-lg">
              <button 
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setShowNewTipModal(false);
                  setNewTipData({ title: '', content: '', category: 'outros', images: [] });
                }}
                disabled={submittingTip || uploadingImage}
              >
                {t('cancel')}
              </button>
              <button 
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={submitNewTip}
                disabled={!newTipData.title.trim() || !newTipData.content.trim() || submittingTip || uploadingImage}
              >
                {submittingTip ? t('ctSending') : t('ctShareTip')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {imagePreviewModal.open && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80" 
          style={{zIndex: 100000}}
          onClick={() => setImagePreviewModal({ open: false, src: '' })}
        >
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            <button
              onClick={() => setImagePreviewModal({ open: false, src: '' })}
              className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 z-10"
            >
              ✕
            </button>
            <img 
              src={imagePreviewModal.src}
              alt="Preview"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityTipsView;
